import type { Emotion, FaceParams } from '../types/emotion';
import { batteryCell, type BatteryState, type CellLook } from './batteryScreen';
import { glyphLit, type FaceGlyph } from './faceGlyph';

export const EMOTION_PARAMS: Record<Emotion, FaceParams> = {
  neutral: { eyeRx: 0.24, eyeRy: 0.3, lid: 0, lidTilt: 0, happyEyes: 0, mouthWidth: 0.4, mouthSmile: 0.15, mouthOpen: 0 },
  curious: { eyeRx: 0.24, eyeRy: 0.28, lid: 0.2, lidTilt: -0.2, happyEyes: 0, mouthWidth: 0.18, mouthSmile: 0, mouthOpen: 0.7 },
  happy: { eyeRx: 0.24, eyeRy: 0.3, lid: 0, lidTilt: 0, happyEyes: 1, mouthWidth: 0.46, mouthSmile: 1, mouthOpen: 0 },
  angry: { eyeRx: 0.26, eyeRy: 0.28, lid: 0.32, lidTilt: 0.6, happyEyes: 0, mouthWidth: 0.38, mouthSmile: -0.8, mouthOpen: 0 },
  sad: { eyeRx: 0.24, eyeRy: 0.28, lid: 0.3, lidTilt: -0.6, happyEyes: 0, mouthWidth: 0.34, mouthSmile: -1, mouthOpen: 0 },
  sleepy: { eyeRx: 0.26, eyeRy: 0.28, lid: 0.62, lidTilt: 0, happyEyes: 0, mouthWidth: 0.2, mouthSmile: 0, mouthOpen: 0 },
  surprised: { eyeRx: 0.29, eyeRy: 0.36, lid: 0, lidTilt: 0, happyEyes: 0, mouthWidth: 0.2, mouthSmile: 0, mouthOpen: 1 },
};

export function lerpParams(current: FaceParams, target: FaceParams, k: number): void {
  (Object.keys(current) as Array<keyof FaceParams>).forEach((key) => {
    current[key] += (target[key] - current[key]) * k;
  });
}

interface DrawOptions {
  size: number;
  grid: number;
  params: FaceParams;
  look: { x: number; y: number };
  open: number;
  time: number;
  /** 0..1 talking mouth opening (while the drone is "speaking" in chat) */
  talk?: number;
  /** 0..1 closes the left eye */
  wink?: number;
  /** Battery screen (charge easter egg); `wipe` 0 = face, 1 = battery, scan line in between */
  charge?: BatteryState & { wipe: number };
  /** Special eyes for the hero play behaviours */
  fx?: EyeFxDraw;
  /** Sign-board mode (quest flights): an icon + scrolling word replace his face */
  glyph?: FaceGlyph | null;
}

export interface EyeFxDraw {
  /** 0..1 eyes wide open with a pupil locked onto `look` */
  wide: number;
  /** 0..1 spiral eyes (dizzy / hypnotised) */
  spiral: number;
  /** 0..1 tight, pulsing spiral */
  tight: number;
  /** spiral rotation phase (rad) */
  spin: number;
  /** 0..1 scanning bar across the eyes */
  scan: number;
  /** 0..1 beating heart eyes */
  heart?: number;
  /** 0..1 big open grin */
  grin?: number;
}

// Face composition, vertically balanced around the display center
const EYE_X = 0.4;
const EYE_Y = 0.33;
const MOUTH_Y = -0.29;

export function drawPixelFace(ctx: CanvasRenderingContext2D, opts: DrawOptions): void {
  const { params: p, look, open, time, size, grid } = opts;
  const cell = size / grid;
  const gap = Math.max(1.5, cell * 0.22);
  const px = cell - gap;
  const half = 1 / grid; // half a cell in face space

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, size, size);

  const charge = opts.charge;
  const wipe = charge ? charge.wipe : 0;
  // Scan line sweeping down while switching face ↔ battery, with a little horizontal glitch
  const scanRow = wipe > 0 && wipe < 1 ? Math.floor(wipe * grid) : -1;
  const glitch = scanRow >= 0 ? Math.round(Math.sin(time * 90) * 1.5) : 0;

  for (let j = 0; j < grid; j++) {
    for (let i = 0; i < grid; i++) {
      const x = ((i + 0.5) / grid) * 2 - 1;
      const y = 1 - ((j + 0.5) / grid) * 2;
      if (x * x + y * y > 0.97) continue;

      let look2: CellLook;
      if (j === scanRow) {
        look2 = 'hot';
      } else if (charge && j < wipe * grid) {
        const gi = Math.abs(j - scanRow) <= 2 ? i + glitch : i;
        look2 = batteryCell(gi, j, grid, charge);
      } else if (opts.glyph) {
        look2 = glyphLit(opts.glyph, i, j, grid, x, y, time) ? 'lit' : 'off';
      } else {
        const lit =
          eye(x, y, -1, p, look, open * (1 - (opts.wink ?? 0)), half, time, opts.fx) ||
          eye(x, y, 1, p, look, open, half, time, opts.fx) ||
          mouth(x, y, p, half, time, opts.talk ?? 0, opts.fx?.grin ?? 0);
        look2 = lit ? 'lit' : 'off';
      }

      const dx = i * cell + gap / 2;
      const dy = j * cell + gap / 2;

      if (look2 === 'hot') {
        ctx.fillStyle = 'rgb(255, 150, 120)';
      } else if (look2 === 'lit') {
        const flicker = 0.9 + 0.1 * Math.sin(time * 6 + j * 0.9);
        ctx.fillStyle = `rgba(255, 52, 36, ${flicker})`;
      } else {
        ctx.fillStyle = 'rgba(255, 24, 24, 0.05)';
      }
      ctx.fillRect(dx, dy, px, px);
    }
  }
}

/** Is this point of his face (face space, -1..1, y up) lit? Used to draw his face full-screen. */
export function faceLit(
  x: number,
  y: number,
  p: FaceParams,
  look: { x: number; y: number },
  open: number,
  half: number,
  time: number,
): boolean {
  return eye(x, y, -1, p, look, open, half, time) || eye(x, y, 1, p, look, open, half, time) || mouth(x, y, p, half, time, 0);
}

function eye(
  x: number,
  y: number,
  side: -1 | 1,
  p: FaceParams,
  look: { x: number; y: number },
  open: number,
  half: number,
  time = 0,
  fx?: EyeFxDraw,
): boolean {
  // Spiral eyes grow out from the centre of each eye and take over completely
  if (fx && fx.spiral > 0.02) {
    const sx = x - (side * EYE_X + look.x * 0.04);
    const sy = y - (EYE_Y + look.y * 0.03);
    const pulse = fx.tight * (0.5 + 0.5 * Math.sin(time * 9));
    const R = (0.27 + 0.05 * pulse) * Math.min(1, fx.spiral * 1.4);
    const r = Math.hypot(sx, sy);
    if (r < R) {
      if (r < half * 1.4) return true;
      // Mirrored per eye (opposite spin) for that properly hypnotised look
      const a = Math.atan2(sy, sx) * side;
      const v = a / (Math.PI * 2) + r * (3 + 3.5 * fx.tight) - (fx.spin * side) / (Math.PI * 2);
      const fr = v - Math.floor(v);
      return fr < 0.42;
    }
    if (fx.spiral > 0.6) return false;
  }

  const wide = fx ? fx.wide : 0;
  const lx = x - (side * EYE_X + look.x * 0.1);
  const ly = y - (EYE_Y + look.y * 0.07);

  // Heart eyes: grow out of each eye and beat (a closed eye — wink — becomes a happy ^ arc)
  const heart = fx?.heart ?? 0;
  if (heart > 0.05) {
    if (open < 0.35) {
      const r = Math.hypot(lx, ly + 0.1);
      return ly > -0.08 && Math.abs(r - 0.2) < half * 1.6;
    }
    const beat = 1 + 0.16 * Math.pow(Math.max(0, Math.sin(time * 7.5)), 6);
    const R = 0.21 * heart * beat;
    const X = lx / R;
    const Y = (ly / R) * 1.05 + 0.2;
    const q = X * X + Y * Y - 1;
    return q * q * q - X * X * Y * Y * Y <= 0;
  }

  // Wide, focused eyes: bigger, lids up, a dark pupil that tracks the cursor
  if (wide > 0.05 && p.happyEyes <= 0.5) {
    const rx = (p.eyeRx + 0.02) * (1 + 0.28 * wide);
    const ry = Math.max(p.eyeRy, 0.3) * (1 + 0.28 * wide) * open;
    if (ry < 0.08) return Math.abs(ly) < half * 1.2 && Math.abs(lx) < rx;
    if ((lx / rx) ** 2 + (ly / ry) ** 2 > 1) return false;
    if (wide > 0.3) {
      const pr = 0.075 + 0.02 * wide;
      const pxo = look.x * rx * 0.45;
      const pyo = look.y * ry * 0.4;
      if (Math.hypot(lx - pxo, ly - pyo) < pr) return false;
    }
    if (fx && fx.scan > 0.5) {
      const bar = Math.sin(time * 4.5) * ry * 0.75;
      if (Math.abs(ly - bar) < half * 1.1) return false;
    }
    return true;
  }

  if (p.happyEyes > 0.5) {
    const r = Math.hypot(lx, (ly + 0.1) / Math.max(open, 0.2));
    return ly > -0.08 && Math.abs(r - 0.22) < half * 1.6;
  }

  const ry = p.eyeRy * open;
  if (ry < 0.08) return Math.abs(ly) < half * 1.2 && Math.abs(lx) < p.eyeRx + 0.02;

  const e = (lx / (p.eyeRx + 0.02)) ** 2 + (ly / ry) ** 2;
  if (e > 1) return false;

  // Inner corner is toward the face center (lx * -side > 0)
  const lidTop = ry - p.lid * 2 * ry - p.lidTilt * lx * -side;
  return ly <= lidTop;
}

function mouth(x: number, y: number, p: FaceParams, half: number, time: number, talk: number, grin = 0): boolean {
  const w = p.mouthWidth;
  const my = MOUTH_Y;

  // Big open grin: a wide "D" — flat top, round bottom, with a gap for the tongue
  if (grin > 0.05) {
    const gw = 0.34 + 0.06 * grin;
    if (Math.abs(x) > gw) return false;
    const u = x / gw;
    const top = my + 0.06;
    const bottom = top - 0.24 * grin * Math.sqrt(Math.max(0, 1 - u * u));
    if (y > top + half || y < bottom - half) return false;
    // Outline + a little tongue at the bottom
    const edge = y > top - half * 1.6 || y < bottom + half * 1.6 || Math.abs(x) > gw - half * 2.2;
    const tongue = Math.hypot(x / 0.13, (y - bottom) / 0.07) < 1;
    return edge || tongue;
  }

  // Talking: an open mouth whose height follows the syllables
  if (talk > 0.08) {
    const tw = Math.max(w, 0.26);
    const ry = 0.05 + talk * 0.18;
    const e = (x / tw) ** 2 + ((y - my) / ry) ** 2;
    return e <= 1 && e >= 0.3;
  }

  if (p.mouthOpen > 0.5) {
    const ry = w * 1.1 * (0.92 + 0.08 * Math.sin(time * 3));
    const e = (x / (w + 0.02)) ** 2 + ((y - my) / ry) ** 2;
    return e <= 1.1 && e >= 0.4;
  }

  if (Math.abs(x) > w + 0.01) return false;
  const yc = my + p.mouthSmile * 0.22 * ((x / w) ** 2 - 0.5);
  return Math.abs(y - yc) < half * 1.6;
}
